import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Button, Divider, Typography, useMediaQuery, useTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import {
  Layout,
  Link,
  Sidebar,
  type SidebarProps,
  useCanAccess,
  useCanAccessResources,
  useGetIdentity,
  useRefresh,
  useSidebarState,
  useTranslate,
} from "react-admin";

import { hasStaffNavigationAccess } from "../auth/access";
import { PortalAccessRefresh } from "../auth/PortalAccessRefresh";
import { CrmGlobalSearch } from "../components/crm/CrmGlobalSearch";
import { CustomUserMenu } from "../components/CustomUserMenu";
import { FeedbackDialog } from "../components/FeedbackDialog";
import { FinanceNotificationBadge } from "../components/finance/FinanceNotificationBadge";
import { StandortSelector } from "../components/StandortSelector";
import { PhoneActivation } from "../components/telephony/PhoneActivation";
import { PhonePanel } from "../components/telephony/PhonePanel";
import { PhoneProvider } from "../components/telephony/PhoneProvider";
import { PhoneSignInCallback } from "../components/telephony/PhoneSignInCallback";
import { ResourceName } from "../constants/resources";
import { useGetEntityName } from "../hooks/useGetEntityName";
import { lineHeight, space, theme, typography } from "../theme/designTokens";
import { BreadcrumbsNav } from "./BreadcrumbsNav";
import { BreadcrumbTitleProvider } from "./BreadcrumbTitleContext";
import { CustomMenu } from "./CustomMenu";

const MainSidebar = (props: SidebarProps) => {
  const { data: identity } = useGetIdentity();
  // The organization and its Standorte belong to organization managers.
  const { canAccess } = useCanAccessResources({
    action: "list",
    resources: [ResourceName.Location, ResourceName.Crm],
  });
  const managesOrganization = canAccess?.[ResourceName.Location] === true && !hasStaffNavigationAccess(canAccess);
  const translate = useTranslate();
  const [sidebarOpen, setSidebarOpen] = useSidebarState();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const showLogo = isMobile || sidebarOpen;
  const closeMobileSidebar = () => {
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <Sidebar
      // A new drawer per breakpoint: reusing the desktop drawer as the phone
      // modal leaves its invisible layer over the page after a resize.
      key={isMobile ? "phone" : "desktop"}
      {...props}
      sx={{
        height: "100dvh",
        "& .RaSidebar-fixed": { height: "100dvh", overflowY: "auto", width: "inherit" },
        "& .MuiDrawer-paper": { backgroundColor: theme.color.white },
      }}
    >
      <Box px={`${space.xs}px`} py={`${space.sm}px`} sx={{ backgroundColor: theme.color.white }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: showLogo ? "space-between" : "center",
          }}
        >
          {showLogo && (
            <Link
              to="/"
              onClick={closeMobileSidebar}
              sx={{ display: "flex", flex: 1, minWidth: 0, justifyContent: "center" }}
            >
              <Box
                component="img"
                src="/images/media4care-logo.png"
                alt="media4care"
                sx={{ display: "block", height: 28, width: "auto", maxWidth: "100%", mx: `${space.xs}px` }}
              />
            </Link>
          )}
          {isMobile ? (
            <IconButton
              data-testid="navigation-close"
              aria-label={translate("ra.action.close_menu")}
              onClick={() => setSidebarOpen(false)}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <CloseIcon />
            </IconButton>
          ) : (
            <IconButton
              data-testid="sidebar-toggle"
              aria-label={translate(sidebarOpen ? "ra.action.close_menu" : "ra.action.open_menu")}
              title={translate(sidebarOpen ? "ra.action.close_menu" : "ra.action.open_menu")}
              aria-expanded={sidebarOpen}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          )}
        </Box>
        {managesOrganization && identity?.organizationName && (
          <Typography
            data-testid="sidebar-organization-name"
            px={`${space.xs}px`}
            pt={`${space.xs}px`}
            sx={{
              display: "block",
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              lineHeight: lineHeight.xxs,
              color: theme.color.typographyBase,
            }}
          >
            {identity.organizationName}
          </Typography>
        )}
        <Divider sx={{ m: `${space.xs}px` }} />
        {managesOrganization && (
          <>
            <StandortSelector onLocationSelected={closeMobileSidebar} />
            <Divider sx={{ m: `${space.xs}px` }} />
          </>
        )}
        <CustomMenu />
      </Box>
    </Sidebar>
  );
};

const MainLayoutContent = ({ children }: { children: ReactNode }) => {
  const refresh = useRefresh();
  const translate = useTranslate();
  const entityName = useGetEntityName();
  const [sidebarOpen, setSidebarOpen] = useSidebarState();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const { canAccess: canSearchCrm } = useCanAccess({ action: "list", resource: ResourceName.Crm });
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const openFeedbackDialog = useCallback(() => setIsFeedbackDialogOpen(true), []);
  const closeFeedbackDialog = useCallback(() => setIsFeedbackDialogOpen(false), []);

  return (
    <Layout
      appBar={() => null}
      appBarAlwaysOn
      sx={{
        backgroundColor: theme.color.white,
        minWidth: 0,
        maxWidth: "100%",
        "& .RaLayout-appFrame": {
          marginTop: "0 !important",
        },
        "& .RaLayout-content": {
          minWidth: 0,
          padding: 0,
        },
      }}
      sidebar={MainSidebar}
    >
      <PortalAccessRefresh />
      <PhoneSignInCallback />
      <PhoneProvider>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            m: { xs: `${space.xl}px`, sm: `${space.xxl}px`, md: `${space.xxl1}px` },
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: `${space.sm}px` }}>
            {isMobile && (
              <IconButton
                data-testid="navigation-toggle"
                aria-label={translate(sidebarOpen ? "ra.action.close_menu" : "ra.action.open_menu")}
                aria-expanded={sidebarOpen}
                onClick={() => setSidebarOpen(!sidebarOpen)}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flex: 1, minWidth: 0, flexBasis: { xs: "100%", sm: "auto" }, order: { xs: 3, sm: 1 } }}>
              <BreadcrumbsNav />
            </Box>
            {canSearchCrm && (
              <Box
                sx={{
                  flex: 1,
                  flexBasis: { xs: "100%", lg: "auto" },
                  display: "flex",
                  justifyContent: "center",
                  minWidth: 0,
                  order: { xs: 4, lg: 2 },
                }}
              >
                <CrmGlobalSearch />
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", ml: "auto", order: { xs: 2, lg: 3 } }}>
              <FinanceNotificationBadge />
              <PhoneActivation />
              <Button
                startIcon={<FeedbackOutlinedIcon />}
                onClick={openFeedbackDialog}
                data-testid="send-feedback-button"
                aria-label={translate("feedback.action")}
                title={translate("feedback.action")}
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  px: { xs: `${space.sm}px`, sm: `${space.xl}px` },
                  "& .MuiButton-startIcon": {
                    ml: { xs: 0, sm: `-${space.xs}px` },
                    mr: { xs: 0, sm: `${space.sm}px` },
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  {translate("feedback.action")}
                </Box>
              </Button>
              <IconButton
                data-testid="header-refresh-button"
                aria-label={translate("ra.action.refresh")}
                onClick={refresh}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <RefreshIcon />
              </IconButton>
              <CustomUserMenu />
            </Box>
          </Box>
          <Typography
            variant="h4"
            align="left"
            marginY={2}
            sx={{ fontSize: { xs: 24, sm: 34 }, overflowWrap: "anywhere" }}
          >
            {entityName}
          </Typography>
          {children}
          <FeedbackDialog open={isFeedbackDialogOpen} onClose={closeFeedbackDialog} />
        </Box>
        <PhonePanel />
      </PhoneProvider>
    </Layout>
  );
};

// The provider sits above the breadcrumb and the page, so a page can name its last crumb.
export const MainLayout = ({ children }: { children: ReactNode }) => (
  <BreadcrumbTitleProvider>
    <MainLayoutContent>{children}</MainLayoutContent>
  </BreadcrumbTitleProvider>
);
